/* ============================================================
   Which URLs the new landing site owns.
   ============================================================

   This exists because of how React Router resolves routes. The host app
   already declares "/", "/login", "/pricing", "/features/..." and ~130 more,
   and router v6 ranks by specificity rather than declaration order — so a
   catch-all <Route path="/*"> mounted alongside them would NEVER win for any
   URL the host also declares. Dropping the module in that way would have
   shown the new home page to precisely nobody.

   So ownership is decided before routing, not during it: <LandingSwitch>
   checks the current pathname against this list and renders EITHER the new
   site's routes OR the host's, never both.

   Anything not listed here — /dashboard, /billing, /main-admin, /partner,
   CRM, HRMS, and every API-driven app screen — always falls through to the
   host, whatever the flag says.

   Adding a page to the new site? Add its path in TWO places: the <Route> in
   LandingRoutes.jsx and the entry here. `npm run check:routes` fails the
   build if the two ever drift apart.
   ============================================================ */

/* ':param' matches exactly one non-empty segment. Everything else is literal. */
export const LANDING_ROUTE_PATHS = [
  /* home */
  '/',

  /* product */
  '/monitoring',
  '/pricing',
  '/contact',
  '/download',
  '/download/all',
  '/hrms',
  '/crm',
  '/email-insights',
  '/pattern-intelligence',
  '/book-demo',
  '/book-demo/confirm',

  /* features */
  '/features',
  '/features/ask-teamgrid',
  '/features/automatic-time-tracking',
  '/features/productivity-scoring',
  '/features/ai-work-summaries',
  '/features/app-website-tracking',
  '/features/hourly-breakdown',
  '/features/team-management',
  '/features/project-tracking',
  '/features/org-intelligence',
  '/features/ai-powered-insights',
  '/features/performance-metrics',

  /* solutions */
  '/solutions',
  '/solutions/remote-team-productivity',
  '/solutions/hr-staffing-firms',
  '/solutions/edtech-productivity-software',
  '/solutions/accounting-firm-productivity',
  '/solutions/project-based-teams',
  '/solutions/virtual-support-services',
  '/solutions/operations-execution-teams',
  '/solutions/bpo-shared-services',
  '/solutions/contract-freelance-teams',
  '/solutions/client-driven-work',
  '/solutions/remote-distributed-teams',
  '/solutions/edtech-teams',
  '/solutions/accounting-firms',
  '/solutions/operations-and-executions',
  '/solutions/client-driven-work-teams',

  /* comparisons */
  '/compare',
  '/compare/teamgrid-ai-vs-employee-monitoring-software',
  '/compare/teamgrid-ai-vs-we360-ai',
  '/compare/teamgrid-ai-vs-insightful',
  '/compare/teamgrid-ai-vs-prohance',
  '/compare/teamgrid-ai-vs-time-doctor',
  '/compare/teamgrid-ai-vs-hubstaff',
  '/compare/teamgrid-ai-vs-activtrak',
  '/comparison/workforce-platform',
  '/comparison/we360',
  '/comparison/insightful',
  '/comparison/prohance',
  '/comparison/time-doctor',
  '/comparison/hubstaff',
  '/comparison/activtrak',

  /* SEO landings */
  '/employee-monitoring-software',
  '/time-tracking-software',

  /* company & content */
  '/about',
  '/blog',
  '/blog-post',
  '/blog/:slug',
  '/careers',
  '/help',
  '/partners',

  /* legal */
  '/privacy',
  '/security',
  '/terms',

  /* auth & account */
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password/:token',
  '/verify-email',
  '/invite',
  '/partner-login',

  /* post-submission */
  '/thanks-demo-request',
  '/thanks-contact-us',

  /* Rebuilt in the landing's design so the site stops switching to the host's
     dark header mid-visit. Copy, case-study figures and the ROI maths are
     carried over verbatim from the host module. */
  '/resource-optimizer',
  '/resource-optimizer/book',

  /* retired, redirected to the host's Resource Optimizer */
  '/managed-services',
  '/managed-services/book',

  /* legacy live-site URLs kept alive */
  '/contact-us',
  '/privacy-policy',
  '/terms-of-service',
  '/home',
  '/index',
  '/landing',
  '/ai-workforce-intelligence',
  '/landing-alt',
  '/alternate',
];

/* The auth screens paint their own full-page layout (split panel, own brand
   mark), so the marketing footer doesn't belong under them. Listed separately
   rather than inferred, and checked by the same matcher as everything else. */
export const AUTH_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password/:token',
  '/verify-email',
  '/invite',
  '/partner-login',
];

/** Normalise: strip query/hash, collapse repeats, drop the trailing slash. */
function segments(pathname) {
  return String(pathname || '/')
    .split(/[?#]/)[0]
    .split('/')
    .filter(Boolean);
}

/** Case-insensitive on literal segments, so /Pricing matches /pricing. */
function matchesAny(patterns, pathname) {
  const parts = segments(pathname);
  return patterns.some((pattern) => {
    if (pattern.length !== parts.length) return false;
    return pattern.every((seg, i) =>
      seg.startsWith(':') ? parts[i].length > 0 : seg.toLowerCase() === parts[i].toLowerCase(),
    );
  });
}

const PATTERNS = LANDING_ROUTE_PATHS.map(segments);
const AUTH_PATTERNS = AUTH_PATHS.map(segments);

/** Does the new landing site own this URL? */
export function ownsPath(pathname) {
  return matchesAny(PATTERNS, pathname);
}

/** Is this one of the full-page auth screens (no marketing footer)? */
export function isAuthPath(pathname) {
  return matchesAny(AUTH_PATTERNS, pathname);
}

export default LANDING_ROUTE_PATHS;
